// Red counts of the Purchase module show only what is new: items that arrived
// on a page of the Purchase panel (or changed status there) since the user last
// opened that page. The items seen are kept per user, role and page in this
// browser's localStorage; without it every item simply counts as new.
import { useEffect, useState } from 'react';

const STORAGE_KEY = 'purchaseSeen';
const CHANGE_EVENT = 'purchase-seen-change';

const readStore = () => {
    try {
        return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {};
    } catch (error) {
        return {};
    }
};

// scope = the panel's role ('requester', 'pm', 'admin', ...), per signed-in user.
const scopeKey = (scope) => `${localStorage.getItem('userId') || 'guest'}:${scope}`;

const readScope = (scope) => readStore()[scopeKey(scope)] || {};

// A status change makes an item new again.
const seenKeyOf = (item) => `${item._id || item.orderNumber || ''}:${item.status || item.deliveryStatus || ''}`;

export const countUnseen = (seenKeys = [], items = []) => {
    const seen = new Set(seenKeys);
    return items.filter((item) => !seen.has(seenKeyOf(item))).length;
};

// Everything on the page is seen once it is open.
export const markPurchaseItemsSeen = (scope, page, items = []) => {
    const keys = items.map(seenKeyOf);
    const store = readStore();
    const scoped = store[scopeKey(scope)] || {};
    const previous = scoped[page] || [];
    if (previous.length === keys.length && keys.every((key) => previous.includes(key))) return;

    store[scopeKey(scope)] = { ...scoped, [page]: keys };
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
    } catch (error) {
        return;
    }
    window.dispatchEvent(new Event(CHANGE_EVENT));
};

// { page: seen keys } for one scope, updated whenever a page is marked seen
// (also from another tab).
export function usePurchaseSeen(scope) {
    const [seen, setSeen] = useState(() => readScope(scope));

    useEffect(() => {
        const refresh = () => setSeen(readScope(scope));
        refresh();
        window.addEventListener(CHANGE_EVENT, refresh);
        window.addEventListener('storage', refresh);
        return () => {
            window.removeEventListener(CHANGE_EVENT, refresh);
            window.removeEventListener('storage', refresh);
        };
    }, [scope]);

    return seen;
}
